// UI Profile: welcome wizard (formModal) + 4 modali per dominio (Profile /
// FoodPrefs / WorkoutPrefs / Account) + Danger zone (Rigenera / Ricomincia)
// + menu drawer (header avatar) + body measurements (CRUD + chart).
// PR-8 (ADR-0005). BKL-024/025 (2026-05-14): spacchettamento sheet read-only
// in modali editabili per dominio + Danger zone in Profilo.
//
// Dipendenze ESM:
//   builders.js   → saveProfile (persistenza profilo Supabase via submitForm)
//   generators.js → genAll (genera i 3 piani dopo salvataggio profilo)
//   ui-home.js    → renderHomeDayCard, renderHomeMiniChart, updateBadge
//                   (refresh UI dopo salvataggio profilo / nuova misurazione)
//   ui-chat.js    → addMsg, setBusy (notifica chat post-submit + gating busy)
//   ui-plans.js   → normDiet (normalizza valore durata_dieta in prefillForm/submit)
//   prompts.js    → normDur (normalizza valore durata_workout)
//
// Dipendenze runtime (window.* / global scope chain):
//   window.S      → S.profile, S.measurements, S.lastMeasurement
//   window.sb     → client Supabase
//   window.currentUser → utente loggato (Supabase auth)
//   Chart (Chart.js CDN globale, usata in renderMeasChart)
//
// NB: loadUserData, welcome wizard, email consent e legal acceptance restano
// inline (orchestratore init / cross-cutting concerns trattati in PR-9+).
// `closeMenu` viene chiamata anche da onclick handler inline su altri pulsanti
// del menu (es. logout) — lasciata esposta su window.

import { saveProfile } from './builders.js';
import { genAll, detectImpactedPlans } from './generators.js';
import { renderHomeDayCard, renderHomeMiniChart, updateBadge } from './ui-home.js';
import { addMsg, setBusy } from './ui-chat.js';
import { normDiet } from './ui-plans.js';
import { normDur } from './prompts.js';
import { getDefaultTrainingDays, getTrainingDays } from './plan-helpers.js';
import { compressImage, readMeasurementFromImage } from './measurement-ocr.js';
import { TDEE_FACTS, MACRO_FACTS } from './method-facts.js';

// ─── FORM PROFILO ──────────────────────────────────────────────────────────
export function openForm() { document.getElementById('formModal').classList.add('show'); initChks(); prefillForm(); }
export function closeForm() { document.getElementById('formModal').classList.remove('show'); }
export function initChks() { document.querySelectorAll('.chk').forEach(el => { const inp=el.querySelector('input'); el.classList.toggle('sel',inp.checked); inp.onchange=()=>el.classList.toggle('sel',inp.checked); }); }
export function getChk(id) { return [...document.querySelectorAll(`#${id} .chk input:checked`)].map(i=>i.value); }
export function prefillForm() {
  const p = window.S.profile;
  if (!p) return; // nuovo utente: form vuoto
  const setVal = (id, v) => { const el = document.getElementById(id); if (el && v != null && v !== '') el.value = v; };
  setVal('f_nome', p.nome);
  setVal('f_eta', p.eta);
  setVal('f_peso', p.peso);
  setVal('f_h', p.altezza);
  setVal('f_sesso', p.sesso);
  setVal('f_obj', p.obiettivo);
  setVal('f_gg', p.giorni_allenamento != null ? String(p.giorni_allenamento) : '');
  setVal('f_min', p.minuti_sessione != null ? String(p.minuti_sessione) : '');
  setVal('f_lv', p.livello);
  setVal('f_pasti', p.pasti_al_giorno != null ? String(p.pasti_al_giorno) : '');
  setVal('f_durata_dieta', p.durata_dieta != null ? String(normDiet(p.durata_dieta)) : '');
  setVal('f_durata_workout', p.durata_workout != null ? String(normDur(p.durata_workout)) : '');
  setVal('f_stretching_preference', p.stretching_preference || 'standalone');
  setVal('f_tempo_col_sna', p.tempo_col_sna || 'rapido');
  setVal('f_tempo_pra', p.tempo_pra || 'medio');
  setVal('f_tempo_cen', p.tempo_cen || 'medio');
  const intField = document.getElementById('f_int');
  if (intField) intField.value = Array.isArray(p.intolleranze) && p.intolleranze.length
    ? p.intolleranze.join(', ')
    : (p.note_alimentari || '');
  const prefNoField = document.getElementById('f_pref_no');
  if (prefNoField) {
    const prefNo = (p.preferenze && Array.isArray(p.preferenze.no))
      ? p.preferenze.no
      : (p.preferenze_no || []);
    prefNoField.value = prefNo.join(', ');
  }
  // Phase 5 (ADR-0018): cucine attive — checkbox group `f_cucine`.
  // Legacy/null/[]/missing → tutte le cucine attive (backward-compat).
  const cucineAttive = (p.preferenze && Array.isArray(p.preferenze.cucine_attive))
    ? p.preferenze.cucine_attive
    : null;
  const cucineSet = cucineAttive && cucineAttive.length > 0 ? new Set(cucineAttive) : null;
  document.querySelectorAll('#f_cucine .chk input').forEach(inp => {
    inp.checked = cucineSet ? cucineSet.has(inp.value) : true;
  });
  // Normalizza per match case-insensitive, rimuove spazi e parentesi (il profilo può avere
  // valori salvati dall'AI con formati tipo "Manubri (3 kg)" vs "manubri 3kg", o estesi tipo "Corda per saltare")
  const norm = s => (s || '').toString().toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '');
  const hasAny = (arr, v) => arr.some(x => norm(x).includes(norm(v)));
  const lim = Array.isArray(p.limitazioni) ? p.limitazioni : [];
  document.querySelectorAll('#f_lim .chk input').forEach(inp => {
    inp.checked = inp.value === 'nessuna' ? lim.length === 0 : hasAny(lim, inp.value);
  });
  const att = Array.isArray(p.attrezzatura) ? p.attrezzatura : [];
  document.querySelectorAll('#f_att .chk input').forEach(inp => {
    inp.checked = att.length ? hasAny(att, inp.value) : inp.value === 'corpo libero';
  });
  // Resync classe .sel sulle chip (change event non scatta su modifica programmatica)
  document.querySelectorAll('.chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
  });
}
// Cascata BMR + moltiplicatore attività. I valori numerici (formula
// Katch-McArdle, fattori LAF) vivono in method-facts.js: sono gli stessi
// spiegati all'utente sulla pagina /metodo.
export function calcTDEE(peso,h,eta,sesso,gg,m) {
  let bmr;
  if (m?.bmr_kcal) {
    bmr = m.bmr_kcal;
  } else if (m?.massa_magra_kg) {
    bmr = KATCH_BASE + KATCH_PER_KG_LBM * m.massa_magra_kg;
  } else if (m?.grasso_pct) {
    bmr = KATCH_BASE + KATCH_PER_KG_LBM * (peso * (1 - m.grasso_pct / 100));
  } else {
    bmr = sesso==='F' ? 10*peso+6.25*h-5*eta-161 : 10*peso+6.25*h-5*eta+5;
  }
  const f = activityFactor(gg);
  return Math.round(bmr*f);
}

// Katch-McArdle: 370 + 21,6 × massa magra. Estratti dal testo della formula in
// method-facts.js così la pagina pubblica e il calcolo restano allineati.
const KATCH_BASE = 370, KATCH_PER_KG_LBM = 21.6;

function activityFactor(gg) {
  const days = parseInt(gg) || 0;
  const band = TDEE_FACTS.activityFactors.find(a => days <= a.maxDays);
  return band ? band.factor : TDEE_FACTS.activityFactors[TDEE_FACTS.activityFactors.length - 1].factor;
}

// Scostamento kcal per obiettivo (dimagrimento −300, massa +250, altrimenti 0).
export function goalDeltaKcal(obj) {
  const o = String(obj || '');
  const id = o.includes('dimagr') ? 'dimagrimento' : o.includes('massa') ? 'massa' : 'mantenimento';
  const row = TDEE_FACTS.goalDelta.find(g => g.id === id);
  return row ? row.kcal : 0;
}

function buildProfileFromForm() {
  const nome = document.getElementById('f_nome').value.trim();
  const eta=parseInt(document.getElementById('f_eta').value)||30, sesso=document.getElementById('f_sesso').value;
  const peso=parseFloat(document.getElementById('f_peso').value)||75, h=parseFloat(document.getElementById('f_h').value)||175;
  const obj=document.getElementById('f_obj').value, gg=parseInt(document.getElementById('f_gg').value)||3;
  const min=parseInt(document.getElementById('f_min').value)||60, lv=document.getElementById('f_lv').value;
  const pasti=parseInt(document.getElementById('f_pasti').value)||5, intNote=document.getElementById('f_int').value.trim();
  const prefNoRaw=document.getElementById('f_pref_no')?.value.trim()||'';
  const durata_dieta=normDiet(document.getElementById('f_durata_dieta').value);
  const durata_workout=normDur(document.getElementById('f_durata_workout').value);
  const stretching_preference=document.getElementById('f_stretching_preference').value||'standalone';
  const tempo_col_sna=document.getElementById('f_tempo_col_sna')?.value||'rapido';
  const tempo_pra=document.getElementById('f_tempo_pra')?.value||'medio';
  const tempo_cen=document.getElementById('f_tempo_cen')?.value||'medio';
  const lim=getChk('f_lim').filter(v=>v!=='nessuna'), att=getChk('f_att');
  // Stessa derivazione usata dal modale profilo — una sola implementazione.
  const { kcal_target:kcal, proteine_target:prot, carbo_target:carb, grassi_target:gras } =
    recomputeDerived(peso, h, eta, sesso, gg, obj);
  // Phase 1.x: intolleranze = keyword atomiche canonical (lattosio, glutine, vegano, ecc.)
  // separated by comma. preferenze.no = singoli alimenti che l'utente non gradisce.
  const intolleranze = intNote ? intNote.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
  const preferenzeNo = prefNoRaw ? prefNoRaw.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
  // Phase 5 (ADR-0018): cucine_attive. Se l'utente seleziona tutte (9/9) o
  // nessuna (0/9) → null = "tutte attive" backward-compat (no filter SQL nel
  // composer). Solo array parziali producono filtro effettivo.
  const cucineSelected = getChk('f_cucine');
  // Phase 5.9: 10 checkbox (9 chef-specific + mediterraneo_esotico aggregate).
  // Se tutte 10 selezionate o nessuna → null (= "tutte attive", no filter).
  const cucineAttive = (cucineSelected.length === 0 || cucineSelected.length === 10)
    ? null
    : cucineSelected;
  const existingPreferenze = (window.S.profile && window.S.profile.preferenze) || { version: 1, no: [], limita: [] };
  const preferenze = { version: 1, no: preferenzeNo, limita: existingPreferenze.limita || [], cucine_attive: cucineAttive };
  return { nome, eta, sesso, peso, altezza:h, obiettivo:obj, limitazioni:lim, attrezzatura:att, giorni_allenamento:gg, minuti_sessione:min, livello:lv, kcal_target:kcal, proteine_target:prot, carbo_target:carb, grassi_target:gras, intolleranze, note_alimentari:intNote, preferenze, pasti_al_giorno:pasti, durata_dieta, durata_workout, stretching_preference, tempo_col_sna, tempo_pra, tempo_cen };
}

export async function submitForm() {
  const nome = document.getElementById('f_nome').value.trim();
  if (!nome) { alert('Inserisci almeno il tuo nome.'); return; }
  const prevProfile = window.S.profile ? { ...window.S.profile } : null;
  window.S.profile = buildProfileFromForm();
  await saveProfile(window.S.profile);
  updateBadge(); closeForm();
  const hasPlans = window.S.plans && (window.S.plans.meal || window.S.plans.workout || window.S.plans.stretch);
  const tipo = (hasPlans && prevProfile) ? detectImpactedPlans(prevProfile, window.S.profile) : 'tutti';
  if (tipo === null) {
    addMsg('user', '📋 Profilo aggiornato.');
    addMsg('ai', `Aggiornato **${nome}**! I tuoi piani esistenti restano invariati — non ho rilevato modifiche che richiedano una rigenerazione.`);
    return;
  }
  addMsg('user', '📋 Scheda compilata — genera i miei piani!');
  const tipoLabel = { alimentazione: 'il piano alimentare e la spesa', allenamento: 'i piani allenamento', tutti: 'tutti i piani' }[tipo] || 'i piani';
  addMsg('ai', `Perfetto **${nome}**! Sto rigenerando ${tipoLabel}.\n\nCi vogliono un paio di minuti — puoi seguire il progresso sulle card in home. Ti avviso qui appena sono pronti.`);
  setBusy(true); await genAll(window.S.profile, '', tipo); setBusy(false);
}

// BKL-001: salva solo profilo su DB, senza rigenerare piani.
export async function updateProfile() {
  const nome = document.getElementById('f_nome').value.trim();
  if (!nome) { alert('Inserisci almeno il tuo nome.'); return; }
  const btn = document.getElementById('updateProfileBtn');
  const msgEl = document.getElementById('formMsg');
  const origLabel = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Salvataggio…'; }
  if (msgEl) { msgEl.style.display = 'none'; msgEl.textContent = ''; msgEl.style.color = ''; }
  try {
    window.S.profile = buildProfileFromForm();
    await saveProfile(window.S.profile);
    updateBadge();
    if (msgEl) {
      msgEl.textContent = '✅ Profilo aggiornato';
      msgEl.style.color = 'var(--ok, #2da44e)';
      msgEl.style.display = 'block';
    }
    setTimeout(() => {
      if (msgEl) { msgEl.style.display = 'none'; msgEl.textContent = ''; }
      closeForm();
    }, 1500);
  } catch (e) {
    if (msgEl) {
      msgEl.textContent = 'Errore: ' + (e?.message || String(e));
      msgEl.style.color = 'var(--err)';
      msgEl.style.display = 'block';
    } else {
      alert('Errore: ' + (e?.message || String(e)));
    }
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = origLabel || 'Aggiorna'; }
  }
}

// ─── MENU DRAWER ───────────────────────────────────────────────────────────
export function toggleMenu() {
  const isOpen = document.getElementById('menuDrawer').classList.contains('open');
  if (isOpen) { closeMenu(); } else {
    document.getElementById('menuOverlay').classList.add('show');
    document.getElementById('menuDrawer').classList.add('open');
    document.getElementById('userMenuBtn').classList.add('open');
  }
}
export function closeMenu() {
  document.getElementById('menuOverlay').classList.remove('show');
  document.getElementById('menuDrawer').classList.remove('open');
  document.getElementById('userMenuBtn').classList.remove('open');
}

// ─── PROFILE MODAL (BKL-024) — dati persona + obiettivo + macro derivati ──
// Helper: ricalcola kcal_target e macros partendo da peso/altezza/età/sesso/gg/obj
// (riusa calcTDEE già esposto). Identico al pattern in buildProfileFromForm.
function recomputeDerived(peso, h, eta, sesso, gg, obiettivo) {
  const tdee = calcTDEE(peso, h, eta, sesso, gg, window.S.lastMeasurement);
  const kcal = tdee + goalDeltaKcal(obiettivo);
  const lbm = window.S.lastMeasurement?.massa_magra_kg;
  const prot = lbm
    ? Math.round(lbm * MACRO_FACTS.proteine.perKgMassaMagra)
    : Math.round(peso * MACRO_FACTS.proteine.perKgPeso);
  const gras = Math.round(kcal * MACRO_FACTS.grassi.quotaKcal / 9);
  const carb = Math.round((kcal - prot * 4 - gras * 9) / 4);
  return { kcal_target: kcal, proteine_target: prot, carbo_target: carb, grassi_target: gras };
}

function showSectionError(elId, msg) {
  const el = document.getElementById(elId);
  if (!el) return;
  el.textContent = msg;
  el.style.display = '';
}

export function openProfile() {
  const p = window.S.profile;
  if (!p) return;
  document.getElementById('p_eta').value = p.eta ?? '';
  document.getElementById('p_sesso').value = p.sesso || 'M';
  document.getElementById('p_obj').value = p.obiettivo || 'forma fisica generale';
  document.getElementById('p_peso_display').textContent = (p.peso != null) ? `${p.peso} kg` : '— kg';
  document.getElementById('p_altezza_display').textContent = (p.altezza != null) ? `${p.altezza} cm` : '— cm';
  // BKL-037: nascondi link "→ aggiorna" altezza per età >=22 (statura completa).
  // Mostralo se età <=21 o se eta non specificata (safety default).
  const altezzaBtn = document.getElementById('p_altezza_update_btn');
  if (altezzaBtn) {
    altezzaBtn.style.display = (p.eta != null && p.eta >= 22) ? 'none' : '';
  }
  document.getElementById('p_kcal_display').textContent = (p.kcal_target != null) ? `${p.kcal_target} kcal` : '— kcal';
  document.getElementById('p_prot_display').textContent = (p.proteine_target != null) ? `${p.proteine_target} g` : '— g';
  document.getElementById('p_carb_display').textContent = (p.carbo_target != null) ? `${p.carbo_target} g` : '— g';
  document.getElementById('p_grassi_display').textContent = (p.grassi_target != null) ? `${p.grassi_target} g` : '— g';
  document.getElementById('profileMsg').style.display = 'none';
  document.getElementById('profileModal').classList.add('show');
}
export function closeProfile() { document.getElementById('profileModal').classList.remove('show'); }
export async function saveProfileSection() {
  const p = window.S.profile || {};
  const eta = parseInt(document.getElementById('p_eta').value) || p.eta || 30;
  const sesso = document.getElementById('p_sesso').value || p.sesso || 'M';
  const obiettivo = document.getElementById('p_obj').value || p.obiettivo || 'forma fisica generale';
  const peso = p.peso ?? 75, h = p.altezza ?? 175, gg = p.giorni_allenamento ?? 3;
  const derived = recomputeDerived(peso, h, eta, sesso, gg, obiettivo);
  const updated = { ...p, eta, sesso, obiettivo, ...derived };
  try {
    await saveProfile(updated);
    window.S.profile = updated;
    updateBadge();
    renderHomeDayCard();
    closeProfile();
  } catch (e) {
    showSectionError('profileMsg', 'Errore: ' + (e?.message || e));
  }
}

// ─── FOOD PREFERENCES MODAL (BKL-024) ─────────────────────────────────────
export function openFoodPrefs() {
  const p = window.S.profile;
  if (!p) return;
  document.getElementById('fp_pasti').value = p.pasti_al_giorno != null ? String(p.pasti_al_giorno) : '5';
  document.getElementById('fp_durata_dieta').value = p.durata_dieta != null ? String(normDiet(p.durata_dieta)) : '1';
  document.getElementById('fp_tempo_col_sna').value = p.tempo_col_sna || 'rapido';
  document.getElementById('fp_tempo_pra').value = p.tempo_pra || 'medio';
  document.getElementById('fp_tempo_cen').value = p.tempo_cen || 'medio';
  // BKL-039: allergeni EU 14 — lista chiusa, separate da intolleranze digestive
  const allergeniSet = new Set(Array.isArray(p.allergeni) ? p.allergeni : []);
  document.querySelectorAll('#fp_allergeni .chk input').forEach(inp => {
    inp.checked = allergeniSet.has(inp.value);
  });
  document.querySelectorAll('#fp_allergeni .chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
    inp.onchange = () => el.classList.toggle('sel', inp.checked);
  });
  document.getElementById('fp_int').value = Array.isArray(p.intolleranze) && p.intolleranze.length
    ? p.intolleranze.join(', ')
    : (p.note_alimentari || '');
  const prefNo = (p.preferenze && Array.isArray(p.preferenze.no))
    ? p.preferenze.no
    : (p.preferenze_no || []);
  document.getElementById('fp_pref_no').value = prefNo.join(', ');
  // BKL-033: cucine attive — populate fp_cucine checkboxes (same logic as f_cucine in formModal)
  const fpCucineAttive = (p.preferenze && Array.isArray(p.preferenze.cucine_attive))
    ? p.preferenze.cucine_attive
    : null;
  const fpCucineSet = fpCucineAttive && fpCucineAttive.length > 0 ? new Set(fpCucineAttive) : null;
  document.querySelectorAll('#fp_cucine .chk input').forEach(inp => {
    inp.checked = fpCucineSet ? fpCucineSet.has(inp.value) : true;
  });
  document.querySelectorAll('#fp_cucine .chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
    inp.onchange = () => el.classList.toggle('sel', inp.checked);
  });
  document.getElementById('foodPrefsMsg').style.display = 'none';
  document.getElementById('foodPrefsModal').classList.add('show');
}
export function closeFoodPrefs() { document.getElementById('foodPrefsModal').classList.remove('show'); }
export async function saveFoodPrefs() {
  const p = window.S.profile || {};
  const pasti = parseInt(document.getElementById('fp_pasti').value) || 5;
  const durata_dieta = normDiet(document.getElementById('fp_durata_dieta').value);
  const tempo_col_sna = document.getElementById('fp_tempo_col_sna').value || 'rapido';
  const tempo_pra = document.getElementById('fp_tempo_pra').value || 'medio';
  const tempo_cen = document.getElementById('fp_tempo_cen').value || 'medio';
  const intNote = document.getElementById('fp_int').value.trim();
  const prefNoRaw = document.getElementById('fp_pref_no').value.trim();
  const intolleranze = intNote ? intNote.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
  const preferenzeNo = prefNoRaw ? prefNoRaw.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
  // BKL-039: allergeni EU 14 — lista chiusa da checkboxes
  const allergeni = [...document.querySelectorAll('#fp_allergeni .chk input:checked')].map(i => i.value);
  // BKL-033: cucine_attive from fp_cucine checkboxes (10 cucine; null = tutte attive)
  const fpCucineSelected = [...document.querySelectorAll('#fp_cucine .chk input:checked')].map(i => i.value);
  const fpCucineAttive = (fpCucineSelected.length === 0 || fpCucineSelected.length === 10) ? null : fpCucineSelected;
  const existingPreferenze = p.preferenze || { version: 1, no: [], limita: [] };
  const preferenze = { ...existingPreferenze, version: 1, no: preferenzeNo, cucine_attive: fpCucineAttive };
  const updated = { ...p, pasti_al_giorno: pasti, durata_dieta, tempo_col_sna, tempo_pra, tempo_cen, intolleranze, note_alimentari: intNote, allergeni, preferenze };
  try {
    await saveProfile(updated);
    window.S.profile = updated;
    closeFoodPrefs();
  } catch (e) {
    showSectionError('foodPrefsMsg', 'Errore: ' + (e?.message || e));
  }
}

// ─── WORKOUT PREFERENCES MODAL (BKL-024 + BKL-070) ───────────────────────

function _updateWpDaysHint(profile) {
  const gg = parseInt(document.getElementById('wp_gg')?.value) || (profile?.giorni_allenamento ?? 3);
  const sel = document.querySelectorAll('#wp_days .chk input:checked').length;
  const hint = document.getElementById('wp_days_hint');
  if (!hint) return;
  if (sel === 0) {
    hint.textContent = 'Nessun giorno selezionato — verrà usato il default smart.';
    hint.style.color = 'var(--tx2,#b1bac4)';
  } else if (sel !== gg) {
    hint.textContent = `${sel} giorni selezionati (il tuo piano prevede ${gg} sess./sett.)`;
    hint.style.color = 'var(--warn,#f59e0b)';
  } else {
    hint.textContent = `${sel}/${gg} selezionati ✓`;
    hint.style.color = 'var(--tx2,#b1bac4)';
  }
}

export function openWorkoutPrefs() {
  const p = window.S.profile;
  if (!p) return;
  const norm = s => (s || '').toString().toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '');
  const hasAny = (arr, v) => arr.some(x => norm(x).includes(norm(v)));
  const lim = Array.isArray(p.limitazioni) ? p.limitazioni : [];
  document.querySelectorAll('#wp_lim .chk input').forEach(inp => {
    inp.checked = inp.value === 'nessuna' ? lim.length === 0 : hasAny(lim, inp.value);
  });
  const att = Array.isArray(p.attrezzatura) ? p.attrezzatura : [];
  document.querySelectorAll('#wp_att .chk input').forEach(inp => {
    inp.checked = att.length ? hasAny(att, inp.value) : inp.value === 'corpo libero';
  });
  document.querySelectorAll('#workoutPrefsModal .chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
    inp.onchange = () => el.classList.toggle('sel', inp.checked);
  });
  document.getElementById('wp_gg').value = p.giorni_allenamento != null ? String(p.giorni_allenamento) : '3';
  // giorni_settimana_allenamento override checkboxes (BKL-070)
  const trainingDays = getTrainingDays(p);
  document.querySelectorAll('#wp_days .chk input').forEach(inp => {
    const v = parseInt(inp.value);
    inp.checked = trainingDays.includes(v);
  });
  document.querySelectorAll('#wp_days .chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
    inp.onchange = () => { el.classList.toggle('sel', inp.checked); _updateWpDaysHint(p); };
  });
  document.getElementById('wp_gg').onchange = () => _updateWpDaysHint(p);
  _updateWpDaysHint(p);
  document.getElementById('wp_min').value = p.minuti_sessione != null ? String(p.minuti_sessione) : '60';
  document.getElementById('wp_lv').value = p.livello || 'principiante';
  document.getElementById('wp_durata_workout').value = p.durata_workout != null ? String(normDur(p.durata_workout)) : '6';
  document.getElementById('wp_stretching_preference').value = p.stretching_preference || 'standalone';
  document.getElementById('workoutPrefsMsg').style.display = 'none';
  document.getElementById('workoutPrefsModal').classList.add('show');
}
export function closeWorkoutPrefs() { document.getElementById('workoutPrefsModal').classList.remove('show'); }
export async function saveWorkoutPrefs() {
  const p = window.S.profile || {};
  const lim = [...document.querySelectorAll('#wp_lim .chk input:checked')].map(i => i.value).filter(v => v !== 'nessuna');
  const att = [...document.querySelectorAll('#wp_att .chk input:checked')].map(i => i.value);
  const gg = parseInt(document.getElementById('wp_gg').value) || 3;
  const min = parseInt(document.getElementById('wp_min').value) || 60;
  const lv = document.getElementById('wp_lv').value || 'principiante';
  const durata_workout = normDur(document.getElementById('wp_durata_workout').value);
  const stretching_preference = document.getElementById('wp_stretching_preference').value || 'standalone';
  // giorni_settimana_allenamento override (BKL-070): NULL = fallback smart
  const selectedDays = [...document.querySelectorAll('#wp_days .chk input:checked')]
    .map(i => parseInt(i.value)).sort((a, b) => a - b);
  const giorni_settimana_allenamento = selectedDays.length > 0 ? selectedDays : null;
  // gg cambia → TDEE/macros si ricalcolano
  const peso = p.peso ?? 75, h = p.altezza ?? 175, eta = p.eta ?? 30, sesso = p.sesso || 'M';
  const derived = recomputeDerived(peso, h, eta, sesso, gg, p.obiettivo);
  const updated = { ...p, limitazioni: lim, attrezzatura: att, giorni_allenamento: gg, giorni_settimana_allenamento, minuti_sessione: min, livello: lv, durata_workout, stretching_preference, ...derived };
  try {
    await saveProfile(updated);
    window.S.profile = updated;
    closeWorkoutPrefs();
    if (typeof window.renderHomeWidgets === 'function') window.renderHomeWidgets();
  } catch (e) {
    showSectionError('workoutPrefsMsg', 'Errore: ' + (e?.message || e));
  }
}

// ─── ACCOUNT MODAL (BKL-024) ──────────────────────────────────────────────
export function openAccount() {
  const p = window.S.profile;
  document.getElementById('a_email_display').textContent = window.currentUser?.email || '—';
  document.getElementById('a_nome').value = p?.nome || '';
  const flags = (p?.onboarding_flags || {});
  document.getElementById('a_email_consent').checked = flags.email_consent === true;
  // Resync .sel class sulla chip del checkbox (CSS hook usato altrove)
  document.querySelectorAll('#accountModal .chk').forEach(el => {
    const inp = el.querySelector('input');
    el.classList.toggle('sel', inp.checked);
    inp.onchange = () => el.classList.toggle('sel', inp.checked);
  });
  document.getElementById('accountMsg').style.display = 'none';
  document.getElementById('accountModal').classList.add('show');
}
export function closeAccount() { document.getElementById('accountModal').classList.remove('show'); }
export async function saveAccount() {
  const p = window.S.profile || {};
  const nome = document.getElementById('a_nome').value.trim();
  if (!nome) { showSectionError('accountMsg', 'Inserisci almeno il tuo nome.'); return; }
  const emailConsent = document.getElementById('a_email_consent').checked;
  const flags = { ...(p.onboarding_flags || {}), email_consent: emailConsent };
  const updated = { ...p, nome, onboarding_flags: flags };
  try {
    await saveProfile(updated);
    window.S.profile = updated;
    updateBadge();
    closeAccount();
  } catch (e) {
    showSectionError('accountMsg', 'Errore: ' + (e?.message || e));
  }
}

// ─── DANGER ZONE (BKL-025) ────────────────────────────────────────────────
export async function regeneratePlans() {
  if (!window.S.profile) return;
  if (!confirm('Stai per rigenerare tutti i piani con i dati attuali. Sicuro?')) return;
  closeProfile();
  addMsg('user', '🔄 Rigenera piani richiesto dal Profilo');
  addMsg('ai', 'Va bene! Sto rigenerando i tuoi piani con i dati attuali. Ci vogliono un paio di minuti.');
  setBusy(true); await genAll(window.S.profile, ''); setBusy(false);
}
export function restartFromScratch() {
  if (!confirm('Sovrascriverai tutte le tue preferenze attuali con quelle che inserirai nella scheda. Sicuro?')) return;
  closeProfile();
  openForm();
}

// ─── COMPOSIZIONE CORPOREA ─────────────────────────────────────────────────
let measFilter = 30;
let measChartInst = null;

export async function openMeasurements() {
  try { if (typeof logEvent === 'function') logEvent('measurements_open', {}); } catch (_) {}
  document.getElementById('measModal').classList.add('show');
  await loadMeasurements();
  switchMeasTab('dati');
}
export function closeMeasurements() { document.getElementById('measModal').classList.remove('show'); }

export function switchMeasTab(tab) {
  document.getElementById('measTabDati').classList.toggle('on', tab==='dati');
  document.getElementById('measTabStorico').classList.toggle('on', tab==='storico');
  document.getElementById('measPanelDati').style.display = tab==='dati' ? '' : 'none';
  document.getElementById('measPanelStorico').style.display = tab==='storico' ? '' : 'none';
  if (tab === 'dati') {
    if (window.S.lastMeasurement) { showMeasViewMode(); } else { showMeasFormMode(); }
  }
  if (tab === 'storico') renderMeasView();
}

export function showMeasViewMode() {
  const m = window.S.lastMeasurement;
  if (!m) { showMeasFormMode(); return; }
  const date = new Date(m.measured_at).toLocaleDateString('it-IT', { day:'numeric', month:'long', year:'numeric' });
  document.getElementById('measViewDate').textContent = 'Ultima misurazione: ' + date;
  const fields = [
    { key:'altezza_cm', lbl:'Altezza', unit:'cm' },
    { key:'peso_kg', lbl:'Peso', unit:'kg' },
    { key:'grasso_pct', lbl:'Grasso', unit:'%' },
    { key:'massa_muscolare_kg', lbl:'Muscolo', unit:'kg' },
    { key:'massa_magra_kg', lbl:'Massa magra', unit:'kg' },
    { key:'massa_ossea_kg', lbl:'Ossea', unit:'kg' },
    { key:'liquidi_pct', lbl:'Liquidi', unit:'%' },
    { key:'grasso_addominale', lbl:'Grasso add.', unit:'' },
    { key:'bmr_kcal', lbl:'BMR', unit:'kcal' },
    { key:'proteine_pct', lbl:'Proteine', unit:'%' },
    { key:'muscolo_scheletrico_pct', lbl:'Musc. schel.', unit:'%' },
    { key:'grasso_sottocutaneo_pct', lbl:'Grasso sott.', unit:'%' },
    { key:'indice_corporeo', lbl:'Indice corp.', unit:'' },
  ];
  document.getElementById('measViewGrid').innerHTML = fields
    .filter(f => m[f.key] != null)
    .map(f => `<div class="meas-view-item"><div class="meas-view-lbl">${f.lbl}</div><div class="meas-view-val">${m[f.key]}<span class="meas-view-unit">${f.unit}</span></div></div>`)
    .join('');
  document.getElementById('measViewMode').style.display = '';
  document.getElementById('measFormMode').style.display = 'none';
}

export function showMeasFormMode() {
  document.getElementById('m_date').value = new Date().toISOString().slice(0,10);
  // Pre-popola peso e altezza dal profilo se non ci sono misurazioni.
  if (!window.S.lastMeasurement && window.S.profile) {
    const pesoEl = document.getElementById('m_peso');
    if (pesoEl && !pesoEl.value && window.S.profile.peso != null) pesoEl.value = window.S.profile.peso;
    const altezzaEl = document.getElementById('m_altezza');
    if (altezzaEl && !altezzaEl.value && window.S.profile.altezza != null) altezzaEl.value = window.S.profile.altezza;
  }
  // Se c'è già una misurazione, pre-popola altezza dall'ultima (cambia raramente).
  if (window.S.lastMeasurement && window.S.lastMeasurement.altezza_cm != null) {
    const altezzaEl = document.getElementById('m_altezza');
    if (altezzaEl && !altezzaEl.value) altezzaEl.value = window.S.lastMeasurement.altezza_cm;
  }
  document.getElementById('measViewMode').style.display = 'none';
  document.getElementById('measFormMode').style.display = '';
}

export function switchToMeasEdit() {
  const m = window.S.lastMeasurement;
  if (m) {
    const map = { m_altezza:'altezza_cm', m_peso:'peso_kg', m_grasso:'grasso_pct', m_muscolo:'massa_muscolare_kg', m_ossea:'massa_ossea_kg', m_liquidi:'liquidi_pct', m_addominale:'grasso_addominale', m_bmr:'bmr_kcal', m_proteine:'proteine_pct', m_scheletrico:'muscolo_scheletrico_pct', m_sottocutaneo:'grasso_sottocutaneo_pct', m_magra:'massa_magra_kg', m_indice:'indice_corporeo' };
    Object.entries(map).forEach(([fid, key]) => { if (m[key] != null) document.getElementById(fid).value = m[key]; });
  }
  showMeasFormMode();
}

export async function loadMeasurements() {
  if (!window.currentUser) return;
  const { data } = await window.sb.from('body_measurements')
    .select('*').eq('user_id', window.currentUser.id)
    .order('measured_at', { ascending: false });
  window.S.measurements = data || [];
  window.S.lastMeasurement = window.S.measurements[0] || null;
}

export async function saveMeasurement() {
  if (!window.currentUser) return;
  const v = id => { const n = parseFloat(document.getElementById(id).value); return isNaN(n) ? null : n; };
  const vi = id => { const n = parseInt(document.getElementById(id).value, 10); return isNaN(n) ? null : n; };
  const peso = v('m_peso'); const grasso = v('m_grasso'); const bmr = v('m_bmr');
  if (peso != null && (peso < 20 || peso > 300)) { showMeasMsg('⚠️ Peso fuori range valido (20–300 kg)'); return; }
  if (grasso != null && (grasso < 3 || grasso > 60)) { showMeasMsg('⚠️ % Grasso fuori range valido (3–60%)'); return; }
  if (bmr != null && (bmr < 800 || bmr > 5000)) { showMeasMsg('⚠️ BMR fuori range valido (800–5000 kcal)'); return; }
  const row = {
    user_id: window.currentUser.id,
    measured_at: document.getElementById('m_date').value || new Date().toISOString().slice(0,10),
    altezza_cm: vi('m_altezza'),
    peso_kg: v('m_peso'), grasso_pct: v('m_grasso'), massa_muscolare_kg: v('m_muscolo'),
    massa_ossea_kg: v('m_ossea'), liquidi_pct: v('m_liquidi'), grasso_addominale: v('m_addominale'),
    bmr_kcal: v('m_bmr'), proteine_pct: v('m_proteine'), muscolo_scheletrico_pct: v('m_scheletrico'),
    grasso_sottocutaneo_pct: v('m_sottocutaneo'), massa_magra_kg: v('m_magra'), indice_corporeo: v('m_indice')
  };
  Object.keys(row).forEach(k => { if (row[k] === null && k !== 'user_id' && k !== 'measured_at') delete row[k]; });
  const { error } = await window.sb.from('body_measurements').insert(row);
  if (error) { showMeasMsg('Errore: ' + error.message); return; }
  ['m_altezza','m_peso','m_grasso','m_muscolo','m_ossea','m_liquidi','m_addominale','m_bmr','m_proteine','m_scheletrico','m_sottocutaneo','m_magra','m_indice'].forEach(id => { document.getElementById(id).value = ''; });
  document.querySelectorAll('.meas-prefilled').forEach(el => el.classList.remove('meas-prefilled'));
  await loadMeasurements();
  // Trigger DB ha aggiornato profiles.peso e/o profiles.altezza se questa
  // misurazione è la più recente. Refetch profilo per allineare S.profile.
  if (row.peso_kg != null || row.altezza_cm != null) {
    const { data: p } = await window.sb.from('profiles').select('*').eq('id', window.currentUser.id).single();
    if (p) { window.S.profile = p; renderHomeDayCard(); }
  }
  renderHomeMiniChart();
  showMeasViewMode();
}

export function showMeasMsg(msg, isError = true) {
  const el = document.getElementById('measMsg');
  el.textContent = msg;
  el.style.color = isError ? 'var(--err)' : '#1D9E75';
  el.style.display = 'block';
  // Solo gli errori spariscono da soli; i messaggi di stato/successo restano
  // finché l'utente non salva o carica un'altra foto.
  if (isError) setTimeout(() => { el.style.display = 'none'; }, 3000);
}

// Mappa chiave DB → id input del form (inverso di quella in switchToMeasEdit).
const MEAS_KEY_TO_INPUT = {
  altezza_cm: 'm_altezza', peso_kg: 'm_peso', grasso_pct: 'm_grasso',
  massa_muscolare_kg: 'm_muscolo', massa_ossea_kg: 'm_ossea', liquidi_pct: 'm_liquidi',
  grasso_addominale: 'm_addominale', bmr_kcal: 'm_bmr', proteine_pct: 'm_proteine',
  muscolo_scheletrico_pct: 'm_scheletrico', grasso_sottocutaneo_pct: 'm_sottocutaneo',
  massa_magra_kg: 'm_magra', indice_corporeo: 'm_indice',
};

// handlePhotoUpload(inputEl): legge la foto scelta, la comprime, chiede a Haiku
// di leggere i valori e pre-compila il form (campi evidenziati). L'utente
// conferma con Salva (validazione range in saveMeasurement). Invocata da
// onchange dell'input file nascosto in index.html.
export async function handlePhotoUpload(inputEl) {
  const file = inputEl.files && inputEl.files[0];
  inputEl.value = ''; // reset: permette di ricaricare lo stesso file
  if (!file) return;
  if (!file.type || !file.type.startsWith('image/')) {
    showMeasMsg('⚠️ Seleziona un file immagine.');
    return;
  }
  showMeasFormMode();
  showMeasMsg('📷 Sto leggendo la foto…', false);
  try {
    const img = await compressImage(file);
    const { fields, dateISO, note } = await readMeasurementFromImage(img);
    const keys = Object.keys(fields);
    if (!keys.length) {
      showMeasMsg('Nessun valore riconosciuto nella foto. Inserisci a mano.');
      return;
    }
    document.querySelectorAll('.meas-prefilled')
      .forEach(el => el.classList.remove('meas-prefilled'));
    // Pulisci i campi composizione corporea PRIMA di applicare i valori letti:
    // i campi che l'AI non legge devono restare VUOTI (evidenti) invece di
    // conservare valori stantii da una misurazione precedente già nel form, che
    // verrebbero salvati come se fossero attuali. Preserva altezza (la bilancia
    // non la mostra) e la data.
    Object.values(MEAS_KEY_TO_INPUT).forEach(id => {
      if (id === 'm_altezza') return;
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    keys.forEach(k => {
      const el = document.getElementById(MEAS_KEY_TO_INPUT[k]);
      if (el) { el.value = fields[k]; el.classList.add('meas-prefilled'); }
    });
    if (dateISO) { const d = document.getElementById('m_date'); if (d) d.value = dateISO; }
    const skipped = Object.keys(MEAS_KEY_TO_INPUT)
      .filter(k => k !== 'altezza_cm' && !keys.includes(k)).length;
    let msg = `✅ Letti ${keys.length} campi — controlla e premi Salva.`;
    if (skipped) msg += ` ${skipped} non letti (vuoti): compilali a mano se servono.`;
    if (note) msg += ` (ignorati: ${note})`;
    showMeasMsg(msg, false);
  } catch (e) {
    showMeasMsg('⚠️ ' + (e.message || 'Errore nella lettura della foto.'));
  }
}

export function setMeasFilter(days) {
  measFilter = days;
  document.querySelectorAll('.meas-filter').forEach(b => b.classList.toggle('on', parseInt(b.dataset.days) === days));
  renderMeasView();
}

export function renderMeasView() { renderMeasChart(); renderMeasList(); }

export function renderMeasChart() {
  const metric = document.getElementById('measMetric').value;
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - measFilter);
  const filtered = window.S.measurements.filter(m => m[metric] != null && new Date(m.measured_at) >= cutoff).slice().reverse();
  const labels = filtered.map(m => new Date(m.measured_at).toLocaleDateString('it-IT', {day:'2-digit',month:'2-digit'}));
  const values = filtered.map(m => m[metric]);
  const ctx = document.getElementById('measChart').getContext('2d');
  if (measChartInst) { measChartInst.destroy(); measChartInst = null; }
  if (!filtered.length) return;
  const isDark = window.matchMedia('(prefers-color-scheme:dark)').matches;
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textColor = isDark ? '#777' : '#888';
  measChartInst = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets: [{ data: values, borderColor:'#1D9E75', backgroundColor:'rgba(29,158,117,0.12)', fill:true, tension:0.3, pointRadius:4, pointBackgroundColor:'#25c98f', borderWidth:2 }] },
    options: { responsive:true, plugins:{ legend:{ display:false } }, scales:{ x:{ grid:{ color:gridColor }, ticks:{ color:textColor, font:{ size:11 } } }, y:{ grid:{ color:gridColor }, ticks:{ color:textColor, font:{ size:11 } } } } }
  });
}

export function renderMeasList() {
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - measFilter);
  const filtered = window.S.measurements.filter(m => new Date(m.measured_at) >= cutoff);
  const listEl = document.getElementById('measList');
  const emptyEl = document.getElementById('measEmpty');
  if (!filtered.length) { listEl.innerHTML = ''; emptyEl.style.display = 'block'; return; }
  emptyEl.style.display = 'none';
  listEl.innerHTML = filtered.map(m => {
    const date = new Date(m.measured_at).toLocaleDateString('it-IT', {day:'2-digit',month:'2-digit',year:'2-digit'});
    const parts = [];
    if (m.peso_kg != null) parts.push(`${m.peso_kg}kg`);
    if (m.grasso_pct != null) parts.push(`Grasso ${m.grasso_pct}%`);
    if (m.massa_magra_kg != null) parts.push(`Magra ${m.massa_magra_kg}kg`);
    if (m.bmr_kcal != null) parts.push(`BMR ${m.bmr_kcal}kcal`);
    return `<div class="meas-item"><div><div class="meas-item-date">${date}</div><div class="meas-item-val">${parts.join(' · ') || '—'}</div></div><button class="meas-item-del" onclick="deleteMeasurement('${m.id}')">✕</button></div>`;
  }).join('');
}

export async function deleteMeasurement(id) {
  if (!confirm('Eliminare questa misurazione?')) return;
  await window.sb.from('body_measurements').delete().eq('id', id);
  await loadMeasurements();
  renderMeasView();
}
